import { Box, Button, ButtonGroup, Fab, Paper, TableContainer } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { enUS, plPL } from "@mui/x-data-grid/locales";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Localized, useLocalization } from "@fluent/react";
import { useNavigate } from "react-router-dom";
import { removeCleaningRoom, selectAllCleaningRooms, CleaningRoom } from "./cleaningRoomsSlice";
import AddIcon from '@mui/icons-material/Add';
import { FlexDiv } from "../../app/Utils";
import { selectSettings } from "../settings/settingsSlice";
import { useCallback, useState } from "react";
import { AppId } from "../../app/appTypes";
import { Dialog, DialogActions, DialogContent, DialogContentText } from "@mui/material";
import { QuickSearchToolbar } from "../../app/QuickSearchToolbar";

export const CleaningRoomList = () => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const rows = useAppSelector(selectAllCleaningRooms)
    const [itemToDelete, setItemToDelete] = useState<AppId | undefined>(undefined)

    const getCustomLocaleText = () => {
        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        }
        return enUS.components.MuiDataGrid.defaultProps.localeText;
    }

    const handleGoToClick = useCallback((id: AppId) => {
        navigate(`/measurements/cleaning-rooms/edit/${id}`)
    }, [navigate])

    const handleAddClick = useCallback(() => {
        navigate('/measurements/cleaning-rooms/create')
    }, [navigate])

    const handleDeleteClick = useCallback((id: AppId) => {
        setItemToDelete(id)
    }, [])

    const handleCancelDelete = useCallback(() => {
        setItemToDelete(undefined)
    }, [])

    const handleConfirmDelete = useCallback(() => {
        if (itemToDelete) {
            dispatch(removeCleaningRoom(itemToDelete))
            setItemToDelete(undefined)
        }
    }, [itemToDelete, dispatch])

    const columns: GridColDef<CleaningRoom>[] = [
        {
            field: 'number',
            headerName: l10n.getString('cleaning-room-number', null, 'Numer'),
            width: 150,
        },
        {
            field: 'name',
            headerName: l10n.getString('cleaning-room-name', null, 'Nazwa'),
            flex: 1,
        },
        {
            field: 'city',
            headerName: l10n.getString('cleaning-room-city', null, 'Miasto'),
            width: 180,
        },
        {
            field: 'area',
            headerName: l10n.getString('cleaning-room-area', null, 'Powierzchnia (m²)'),
            width: 150,
        },
        {
            field: 'volume',
            headerName: l10n.getString('cleaning-room-volume', null, 'Objętość (m³)'),
            width: 150,
        },
        {
            field: 'actions',
            type: 'actions',
            width: 200,
            renderCell: (params) => (
                <ButtonGroup>
                    <Button onClick={() => handleGoToClick(params.row.id)} color="secondary" variant="outlined">
                        <Localized id="edit"><span>Edytuj</span></Localized>
                    </Button>
                    <Button color="primary" onClick={() => handleDeleteClick(params.row.id)}>
                        <Localized id="delete"><span>Usuń</span></Localized>
                    </Button>
                </ButtonGroup>
            ),
        },
    ]

    return (
        <Box sx={{ width: '100%' }}>
            <div style={{ display: 'flex', height: '100%' }}>
                <div style={{ flexGrow: 1 }}>
                    <TableContainer component={Paper}>
                        <DataGrid
                            localeText={getCustomLocaleText()}
                            rows={rows}
                            columns={columns}
                            style={{ minHeight: 100, height: 'calc(100vh - 200px)' }}
                            slots={{ toolbar: QuickSearchToolbar }}
                            onRowDoubleClick={({ row }) => handleGoToClick(row.id)}
                        />
                    </TableContainer>
                </div>
            </div>
            <FlexDiv>
                <Fab color="secondary" sx={{ marginTop: 2 }} onClick={handleAddClick}>
                    <AddIcon />
                </Fab>
            </FlexDiv>
            <Dialog open={itemToDelete !== undefined}>
                <DialogContent>
                    <DialogContentText>
                        <Localized id="confirm-delete">
                            <span>Czy napewno chcesz usunąć?</span>
                        </Localized>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmDelete}>
                        <Localized id="yes"><span>Tak</span></Localized>
                    </Button>
                    <Button onClick={handleCancelDelete}>
                        <Localized id="no"><span>Nie</span></Localized>
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
